import type { ViewComponent } from "@frui.ts/views";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import React from "react";
import SubLedgerListViewModel from "../../../viewModels/finance/subLedger/SubLedgerListViewModel";
import type { ColumnDefinition } from "@frui.ts/dataviews";
import { formatDateTime } from "../../../utils/localizations";
import ListView from "../../common/list";
import type SubLedgerReportResponse from "../../../entities/subLedgerReportResponse";
import Button from "@edaz/edazkit/Button/index";
import Icon, { IconType } from "@edaz/edazkit/Icon";
import ReportStatus from "../../../entities/reportStatus";
import SubLedgerListFilters from "./subLedgerListFilters";

export const SubLedgerListView: ViewComponent<SubLedgerListViewModel> = observer(({ vm }) => {
  const cellClassName = "text-nowrap";

  const columns: ColumnDefinition<SubLedgerReportResponse>[] = [
    {
      cellFormatter: () => (
        <td>
          <Icon icon={IconType.Excel} width={24} height={24} style={{ marginRight: "-14px" }} />
        </td>
      ),
    },
    {
      title: vm.translate("items.name"),
      valueFormatter: props => vm.formatReportName(props.item),
    },
    {
      title: vm.translateAttribute("createdAt"),
      property: "createdAt",
      headerClassName: "text-left",
      cellClassName,
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      title: vm.translateAttribute("createdBy"),
      property: "createdBy",
      cellClassName,
    },
    {
      title: vm.translateAttribute("lastDownloadedBy"),
      property: "lastDownloadedBy",
      cellClassName,
    },
    {
      title: vm.translateAttribute("lastDownloadedAt"),
      property: "lastDownloadedAt",
      headerClassName: "text-left",
      cellClassName,
      valueFormatter: props => (props.value ? formatDateTime(props.value) : ""),
    },
    {
      valueFormatter: props => props.item.status,
      cellFormatter: ({ item }) => {
        switch (item.status) {
          case ReportStatus.Processed:
            return (
              <td>
                <Button
                  variant="success"
                  className="rounded"
                  size="sm"
                  iconLeft={IconType.Download}
                  disabled={vm.downloadInProgress.get(item.id)}
                  onClick={() => vm.onDownloadClick(item)}>
                  {vm.translateGeneral("download")}
                </Button>
              </td>
            );
          case ReportStatus.ToBeProcessed:
            return (
              <td>
                <strong className="text-warning">{vm.translate("toBeProcessed")}</strong>
              </td>
            );
          case ReportStatus.ProcessingFailed:
            return (
              <td>
                <strong className="text-danger">{vm.translate("processingFailed")}</strong>
              </td>
            );
        }
      },
    },
  ];

  return (
    <ListView
      columns={columns}
      rowProps={item => {
        if (item.status == ReportStatus.ProcessingFailed) {
          return {
            className: "table-failed",
          };
        }

        return {};
      }}
      vm={vm}
      aboveTable={<SubLedgerListFilters vm={vm} />}
      showDetailWithList={false}
      headerButtons={
        vm.canMakeReport ? (
          <Button className="rounded" size="sm" iconLeft={IconType.SignPlus} onClick={() => vm.onNewReportClick?.()}>
            {vm.translate("newReport")}
          </Button>
        ) : null
      }
    />
  );
});

registerView(SubLedgerListView, SubLedgerListViewModel);
